import { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  /* Import modern fonts */
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Space+Grotesk:wght@400;500;600;700&display=swap');

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  :root {
    /* Modern color palette */
    --primary-50: #f8fafc;
    --primary-100: #f1f5f9;
    --primary-200: #e2e8f0;
    --primary-300: #cbd5e1;
    --primary-400: #94a3b8;
    --primary-500: #64748b;
    --primary-600: #475569;
    --primary-700: #334155;
    --primary-800: #1e293b;
    --primary-900: #0f172a;

    /* Accent colors */
    --accent-50: #fef7ff;
    --accent-100: #fce7ff;
    --accent-200: #f8d4fe;
    --accent-300: #f2b6fc;
    --accent-400: #ea8df9;
    --accent-500: #dd5ff2;
    --accent-600: #c238e3;
    --accent-700: #a123c8;
    --accent-800: #8319a6;
    --accent-900: #6b1887;

    /* Warm accent for Monument Valley theme */
    --warm-50: #fef7ed;
    --warm-100: #fdecd9;
    --warm-200: #fbd8b5;
    --warm-300: #f8bb6c;
    --warm-400: #f59446;
    --warm-500: #f17620;
    --warm-600: #e45a16;
    --warm-700: #c24415;
    --warm-800: #9a3618;
    --warm-900: #7c2e1a;

    /* Typography scale */
    --font-size-xs: 0.75rem;
    --font-size-sm: 0.875rem;
    --font-size-base: 1rem;
    --font-size-lg: 1.125rem;
    --font-size-xl: 1.25rem;
    --font-size-2xl: 1.5rem;
    --font-size-3xl: 1.875rem;
    --font-size-4xl: 2.25rem;
    --font-size-5xl: 3rem;

    /* Line heights */
    --line-height-tight: 1.25;
    --line-height-normal: 1.5;
    --line-height-relaxed: 1.625;

    /* Spacing scale */
    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-5: 1.25rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
    --space-10: 2.5rem;
    --space-12: 3rem;
    --space-16: 4rem;
    --space-20: 5rem;

    /* Border radius */
    --radius-sm: 0.25rem;
    --radius-md: 0.5rem;
    --radius-lg: 0.75rem;
    --radius-xl: 1rem;
    --radius-2xl: 1.5rem;
    --radius-full: 9999px;

    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
    --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
    --shadow-2xl: 0 25px 50px -12px rgb(0 0 0 / 0.25);
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    font-feature-settings: 'cv02', 'cv03', 'cv04', 'cv11';

    @media (max-width: 768px) {
      font-size: 15px;
    }

    @media (max-width: 480px) {
      font-size: 14px;
    }
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
    font-weight: 400;
    line-height: var(--line-height-normal);
    background: linear-gradient(135deg, #fefefe 0%, #f8fafc 50%, #f1f5f9 100%);
    color: var(--primary-800);
    min-height: 100vh;
    overflow-x: hidden;
    -webkit-text-size-adjust: 100%;
    -webkit-tap-highlight-color: transparent;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Display font for headings */
  h1, h2, h3, h4, h5, h6 {
    font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    font-weight: 600;
    line-height: var(--line-height-tight);
    letter-spacing: -0.025em;
  }

  h1 { font-size: var(--font-size-5xl); }
  h2 { font-size: var(--font-size-4xl); }
  h3 { font-size: var(--font-size-3xl); }
  h4 { font-size: var(--font-size-2xl); }
  h5 { font-size: var(--font-size-xl); }
  h6 { font-size: var(--font-size-lg); }

  .App {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  main {
    flex: 1;
    padding: 0;
    width: 100%;
  }

  /* Modern link styles */
  a {
    color: var(--accent-600);
    text-decoration: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;

    &:hover {
      color: var(--accent-700);
      transform: translateY(-1px);
    }

    &:focus {
      outline: 2px solid var(--accent-400);
      outline-offset: 2px;
      border-radius: var(--radius-sm);
    }

    @media (max-width: 480px) {
      display: inline-block;
      min-height: 44px;
      padding: 8px 0;
    }
  }

  /* Modern button styles */
  button {
    font-family: inherit;
    font-weight: 500;
    cursor: pointer;
    border: none;
    background: none;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;

    &:focus {
      outline: 2px solid var(--accent-400);
      outline-offset: 2px;
      border-radius: var(--radius-md);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    @media (max-width: 480px) {
      min-height: 44px;
      min-width: 44px;
    }
  }

  /* Modern input styles */
  input, textarea, select {
    font-family: inherit;
    font-size: var(--font-size-base);
    line-height: var(--line-height-normal);
    color: var(--primary-800);
    background: white;
    border: 1px solid var(--primary-200);
    border-radius: var(--radius-md);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);

    &:focus {
      outline: none;
      border-color: var(--accent-400);
      box-shadow: 0 0 0 3px rgb(234 141 249 / 0.1);
    }

    &::placeholder {
      color: var(--primary-400);
    }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: var(--radius-lg);
  }

  /* Modern scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--primary-50);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--primary-300);
    border-radius: var(--radius-full);
    transition: background 0.2s;

    &:hover {
      background: var(--primary-400);
    }
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: var(--primary-300) var(--primary-50);
  }

  /* Selection styles */
  ::selection {
    background: var(--accent-200);
    color: var(--primary-900);
  }

  ::-moz-selection {
    background: var(--accent-200);
    color: var(--primary-900);
  }

  /* Focus visible for accessibility */
  :focus-visible {
    outline: 2px solid var(--accent-500);
    outline-offset: 2px;
    border-radius: var(--radius-sm);
  }

  /* Better touch targets for mobile */
  @media (max-width: 768px) {
    input, textarea, select {
      min-height: 44px;
      font-size: 16px; /* Prevent zoom on iOS */
    }
  }

  /* Improve scroll performance on mobile */
  @media (max-width: 768px) {
    * {
      -webkit-overflow-scrolling: touch;
    }
  }

  /* Reduce motion for users who prefer it */
  @media (prefers-reduced-motion: reduce) {
    * {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;